import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import uniq from 'lodash-es/uniq';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { BillingQuoteCurrency } from 'uplisting-frontend/models/billing-quote';
import { SecurityDepositPer } from 'uplisting-frontend/models/account-configuration';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { AnalyticsEvents } from 'uplisting-frontend/services/analytics';
import { getCurrencySymbol } from 'uplisting-frontend/utils/currencies';

export default class PricingService extends Service {
  @service intl!: Services['intl'];
  @service session!: Services['session'];
  @service features!: Services['features'];
  @service analytics!: Services['analytics'];
  @service notifications!: Services['notifications'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/account-configuration')
  accountConfigurationRepository!: Services['repositories/account-configuration'];

  @service('repositories/guest-identity-verification-configuration')
  guestIdentityVerificationConfigurationRepository!: Services['repositories/guest-identity-verification-configuration'];

  @tracked isUpgradingPlan = false;

  private clientManagementPrices = {
    GBP: 4,
    EUR: 5,
    USD: 6,
    AUD: 8,
    CAD: 7.5,
    NZD: 8.5,
  };

  private clientStatementPrices = {
    GBP: 4,
    EUR: 5,
    USD: 6,
    AUD: 8,
    CAD: 7.5,
    NZD: 8.5,
  };

  private seamPrices = {
    GBP: 4,
    EUR: 5,
    USD: 6,
    AUD: 8,
    CAD: 7.5,
    NZD: 8.5,
  };

  private securityDepositPrices = {
    booking: {
      GBP: 1.5,
      EUR: 1.75,
      USD: 2,
      AUD: 3,
      CAD: 2.5,
      NZD: 3,
    },

    property: {
      GBP: 4,
      EUR: 5,
      USD: 6,
      AUD: 8,
      CAD: 7.5,
      NZD: 8.5,
    },
  };

  private guestIdentityPrices = {
    full: {
      GBP: 1.25,
      EUR: 1.5,
      USD: 1.65,
      AUD: 2.2,
      CAD: 2.1,
      NZD: 2.4,
    },

    discounted: {
      GBP: 1,
      EUR: 1.2,
      USD: 1.3,
      AUD: 1.75,
      CAD: 1.7,
      NZD: 1.9,
    },
  };

  private dbePlusPrices = {
    GBP: 30,
    EUR: 36,
    USD: 39,
    AUD: 56,
    CAD: 55,
    NZD: 68,
  };

  private adaptPrices = {
    GBP: 8,
    EUR: 9,
    USD: 10,
    AUD: 14,
    CAD: 14,
    NZD: 17,
  };

  @cached
  get supportedCurrencies(): string[] {
    return Object.values(BillingQuoteCurrency);
  }

  @cached
  get customer(): ModelRegistry['customer'] | undefined {
    return (this.session.currentUser as ModelRegistry['user']).customer;
  }

  @cached
  get currencyClass(): string {
    return `${this.currency} bold`;
  }

  @cached
  get currency(): string {
    if (this.customer?.currency) {
      return this.customer.currency.toUpperCase();
    }

    const { properties, supportedCurrencies } = this;

    const currencies = uniq(properties.map((property) => property.currency));

    const relevant = currencies.find((currency) =>
      supportedCurrencies.includes(currency),
    );

    if (relevant) {
      return relevant.toUpperCase();
    }

    return 'USD';
  }

  @cached
  get planVersion(): number | undefined {
    return this.customer?.planVersion;
  }

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get planDuration(): string {
    return this.intl.t(`duration.${this.customer?.annual ? 'year' : 'month'}`);
  }

  @cached
  get clientManagementPrice(): number {
    return this.clientManagementPrices[this.currency] as number;
  }

  @cached
  get clientStatementsPrice(): number {
    const price = this.clientStatementPrices[this.currency] as number;

    if (this.features.isClientManagementEnabledOnAccount) {
      return price;
    }

    return this.clientManagementPrice + price;
  }

  @cached
  get seamPrice(): number {
    return this.seamPrices[this.currency] as number;
  }

  @cached
  get guestIdentityPrice(): number {
    const { currentConfiguration } =
      this.guestIdentityVerificationConfigurationRepository;

    const key = currentConfiguration.discounted ? 'discounted' : 'full';

    return this.guestIdentityPrices[key][this.currency];
  }

  @cached
  get securityDepositPer(): SecurityDepositPer {
    return this.accountConfigurationRepository.currentAccountConfiguration
      .securityDepositPer;
  }

  @cached
  get securityDepositPerTranslationKey(): string {
    return `security_deposit_per.${this.securityDepositPer}`;
  }

  @cached
  get securityDepositPrice(): number {
    return this.securityDepositPrices[this.securityDepositPer][this.currency];
  }

  @cached
  get adaptPrice(): number {
    return this.adaptPrices[this.currency] as number;
  }

  @cached
  get adaptPriceLabel(): string {
    return `${getCurrencySymbol(this.currency)}${this.adaptPrice}`;
  }

  @cached
  get dbePlusPrice(): number {
    return this.dbePlusPrices[this.currency] as number;
  }

  @action
  @waitFor
  async handleUpgradePlan(): Promise<void> {
    const { customer } = this;

    if (!customer) {
      return;
    }

    const prevPlanVersion = customer.planVersion;

    try {
      this.isUpgradingPlan = true;
      customer.planVersion = 0;

      await customer.save();

      this.analytics.trackEvent(AnalyticsEvents.legacyPlanUpgrade);

      this.notifications.info('notifications.plan_upgraded');
    } catch {
      customer.planVersion = prevPlanVersion;
      this.notifications.error();
    } finally {
      this.isUpgradingPlan = false;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    pricing: PricingService;
  }
}
